// Header y barra de estado de la propiedad

import React from 'react';
import { Building2, Users, CreditCard, Clock, HelpCircle } from 'lucide-react';
import { calculateTimeLeft } from '../utils/propertyHelpers';
import CountdownTimer from './CountdownTimer';
import { getLastChanceEndDate } from '../config/lastChanceConfig';
import { getBlindBiddingEndDate } from '../config/blindBiddingConfig';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';

/**
 * Header de la propiedad con título, descripción y barra de estado
 */
export const PropertyHeader = ({
  property,
  propertyStatus,
  statusIcon,
  propertyBids = [],
  propertyInvites = [],
  totalBidders = 0,
  showBidders = false,
  invite = null,
  blindOffersCount = 0,
  translations = null,
}) => {
  const { t } = useTranslation();
  const isUpcoming = propertyStatus === 'UPCOMING';
  const isOpen = propertyStatus === 'OPEN';
  const isLastChance = propertyStatus === 'LAST_CHANCE';
  const isClosed = propertyStatus === 'CLOSED';
  const isBlindWaiting = propertyStatus === 'BLIND_WAITING';
  const isBlindActive = propertyStatus === 'BLIND_ACTIVE';
  const hasBids = propertyBids.length > 0;

  const StatusIcon = statusIcon.icon;

  // ✅ NUEVO: Calcular tiempo restante para última oportunidad
  const lastChanceEnd = isLastChance ? getLastChanceEndDate(property) : null;

  // ✅ NUEVO: Calcular tiempo restante para oferta ciega
  const blindBiddingEnd = isBlindActive ? getBlindBiddingEndDate(property) : null;

  // Nombre del usuario para el saludo
  const userName = invite?.name || null;

  return (
    <div className="mb-6">
      {/* Header con título, descripción y botón FAQ */}
      <div className="flex items-start justify-between gap-3 mb-4">
        {/* Lado izquierdo: Logo y texto */}
        <div className="flex items-center gap-3 flex-1">
          <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-3 rounded-xl flex-shrink-0">
            <Building2 className="w-6 h-6 text-slate-900" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-1">
              {translations?.title ?? property.title}
            </h1>
            <p className="text-sm md:text-base text-slate-400">
              {translations?.description ?? property.description}
            </p>
          </div>
        </div>

        {/* Lado derecho: Saludo + Selector idioma + Botón FAQ */}
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          {userName && (
            <span className="text-xs md:text-sm text-slate-400" title={invite?.email}>
              {t('header.greeting', { name: userName })}
            </span>
          )}
          {property.translationsEnabled && <LanguageToggle />}
          <a
            href="/faqs"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-slate-600/50 rounded-lg transition-all group"
            title={t('header.helpTooltip')}
          >
            <HelpCircle className="w-4 h-4 md:w-5 md:h-5 text-slate-400 group-hover:text-blue-400 transition-colors" />
            <span className="hidden sm:inline text-sm md:text-base text-slate-300 group-hover:text-white transition-colors">
              {t('header.helpButton')}
            </span>
          </a>
        </div>
      </div>

      {/* Barra de estado */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-3 md:p-4 border border-slate-700/50">
        <div className="flex flex-wrap items-center justify-between gap-3 md:gap-4">

          {/* Estado de la propiedad - Lado izquierdo */}
          <div className="flex items-center gap-2">
            {isUpcoming && (
              <>
                <StatusIcon className={`w-4 h-4 md:w-5 md:h-5 ${statusIcon.color}`} />
                <span className={`text-sm md:text-base font-bold ${statusIcon.color}`}>
                  {t('header.status.upcoming')}
                </span>
              </>
            )}

            {isBlindWaiting && (
              <>
                <StatusIcon className={`w-4 h-4 md:w-5 md:h-5 ${statusIcon.color}`} />
                <span className={`text-sm md:text-base font-bold ${statusIcon.color}`}>
                  {t('header.status.blindWaiting')}
                </span>
              </>
            )}

            {isBlindActive && (
              <>
                <StatusIcon className={`w-4 h-4 md:w-5 md:h-5 ${statusIcon.color}`} />
                <span className={`text-sm md:text-base ${statusIcon.color}`}>{t('header.status.blindActive')}</span>
                <span className={`text-sm md:text-base font-bold ${statusIcon.color}`}>
                  <CountdownTimer deadline={blindBiddingEnd} />
                </span>
              </>
            )}

            {isOpen && (
              <>
                <StatusIcon className={`w-4 h-4 md:w-5 md:h-5 ${statusIcon.color}`} />
                <span className="text-sm md:text-base text-slate-300">{t('header.status.timeRemaining')}</span>
                <span className="text-sm md:text-base font-bold text-white">
                  <CountdownTimer deadline={property.deadline} />
                </span>
              </>
            )}

            {/* ✅ NUEVO: Estado LAST_CHANCE con colores amarillos */}
            {isLastChance && (
              <>
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                <span className="text-sm md:text-base text-amber-300">{t('header.status.lastChance')}</span>
                <span className="text-sm md:text-base font-bold text-amber-400">
                  <CountdownTimer deadline={lastChanceEnd} />
                </span>
              </>
            )}

            {isClosed && (
              <>
                <StatusIcon className={`w-4 h-4 md:w-5 md:h-5 ${statusIcon.color}`} />
                <span className={`text-sm md:text-base font-bold ${statusIcon.color}`}>
                  {t('header.status.closed')}
                </span>
              </>
            )}
          </div>
          {/* Badge anti-sniping - Lado derecho */}
          {isOpen && property.original_deadline && (() => {
            const extensionMs = new Date(property.deadline) - new Date(property.original_deadline);
            const isMaxReached = extensionMs >= 60 * 60 * 1000; // 60 minutos en ms

            return (
              <div className="flex items-center">
                <span className={`px-3 py-1 text-sm font-medium rounded-full border ${
                  isMaxReached
                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                    : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                }`}>
                  {isMaxReached
                    ? t('header.antiSniping.maxReached')
                    : t('header.antiSniping.extended')
                  }
                </span>
              </div>
            );
          })()}

          {/* Badge de ofertas ciegas - Lado derecho */}
          {isBlindActive && blindOffersCount >= 2 && (
            <div className="flex items-center">
              <span className="px-3 py-1 text-sm font-medium rounded-full border bg-amber-500/20 text-amber-400 border-amber-500/30">
                {t('header.blindOffersBadge')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyHeader;
